// Per-job level curve L(T_job) = 1000 * (1 - exp(-T_job / tau)).
// See docs/06-level-xp §6.2-6.5 and ADR 0004 §2.2.
import { MAX_LEVEL_ASYMPTOTE, DUNGEON_TIER_LEVEL_FACTOR } from './constants.js';
function assertFinite(name, value) {
    if (!Number.isFinite(value)) {
        throw new RangeError(`${name} must be a finite number, got ${String(value)}`);
    }
}
function assertNonNegative(name, value) {
    assertFinite(name, value);
    if (value < 0) {
        throw new RangeError(`${name} must be >= 0, got ${value}`);
    }
}
function assertPositiveTau(tau) {
    assertFinite('tau', tau);
    if (tau <= 0) {
        throw new RangeError(`tau must be > 0, got ${tau}`);
    }
}
/** Continuous current-job level L(T_job). Returns the unfloored value (>= 0, < 1000). */
export function levelFractionFromTotalXp(totalXp, tau) {
    assertNonNegative('totalXp', totalXp);
    assertPositiveTau(tau);
    return MAX_LEVEL_ASYMPTOTE * (1 - Math.exp(-totalXp / tau));
}
/** Display-level floor(L(T)) per docs §6.2. */
export function levelFromTotalXp(totalXp, tau) {
    return Math.floor(levelFractionFromTotalXp(totalXp, tau));
}
/**
 * Inverse of L(T). XP needed to reach exactly the given continuous level.
 * Throws RangeError if targetLevel >= MAX_LEVEL_ASYMPTOTE (asymptotically unreachable).
 */
export function xpToReachLevel(targetLevel, tau) {
    assertNonNegative('targetLevel', targetLevel);
    assertPositiveTau(tau);
    if (targetLevel >= MAX_LEVEL_ASYMPTOTE) {
        throw new RangeError(`targetLevel must be < ${MAX_LEVEL_ASYMPTOTE} (asymptotically unreachable), got ${targetLevel}`);
    }
    return -tau * Math.log(1 - targetLevel / MAX_LEVEL_ASYMPTOTE);
}
/** XP delta from current totalXp to the next integer display-level. */
export function xpForNextLevel(totalXp, tau) {
    const current = levelFromTotalXp(totalXp, tau);
    const next = current + 1;
    if (next >= MAX_LEVEL_ASYMPTOTE) {
        throw new RangeError(`next level ${next} reaches the asymptote; cannot compute XP-to-next`);
    }
    return xpToReachLevel(next, tau) - totalXp;
}
/** Recommended hero level for a given dungeon tier. docs §6.10. */
export function recommendedLevelForDungeonTier(tier) {
    assertNonNegative('tier', tier);
    return tier * DUNGEON_TIER_LEVEL_FACTOR;
}
//# sourceMappingURL=level.js.map